import { Button } from 'admin/components/atoms/Buttons';
import ConfirmModal from 'admin/components/atoms/ConfirmModal';
import { COLOR } from 'admin/constants/styles';
import React from 'react';
import styled, { css } from 'styled-components';

// 加盟店/店舗の審査フロー（受付 → 審査 → 完了）で共有する UI 部品。
// MerchantApplicationDetail と ShopApplicationDetails の双方で使う。

export type ReviewStatus = 'submitted' | 'reviewing' | 'pending' | 'approved' | 'rejected';

// 内容編集(PATCH)が許可される現在ステータス。
// 運用フロー上、内容の審査・修正は「審査中(reviewing)」に入ってから行う。
export const isReviewEditable = (status: ReviewStatus) => status === 'reviewing';

const STEP_GREEN = COLOR.corporatePrimaryColor;
const STEP_RED = '#d75f48';

// 各ステータスの段（1=受付 / 2=審査(保留含む) / 3=完了）。
const statusStage = (s: ReviewStatus): number =>
  s === 'submitted' ? 1 : s === 'reviewing' || s === 'pending' ? 2 : 3;

// 段の現在色（審査中=青 / 保留中=琥珀 / 完了は承認=緑・却下=赤）。
const STAGE_COLOR: Record<ReviewStatus, string> = {
  submitted: '#64748b',
  reviewing: '#2f80ed',
  pending: '#b7791f',
  approved: STEP_GREEN,
  rejected: STEP_RED,
};

type StepView = {
  mark: string;
  bg: string;
  fg: string;
  border: string;
  title: string;
  titleColor: string;
  titleWeight: number;
};

const makeStep = (pos: number, current: ReviewStatus): StepView => {
  const stg = statusStage(current);
  const title =
    pos === 1
      ? '受付'
      : pos === 2
      ? '審査'
      : current === 'approved'
      ? '承認'
      : current === 'rejected'
      ? '却下'
      : '完了';
  if (stg > pos) {
    // 通過済み
    return {
      mark: '✓',
      bg: STEP_GREEN,
      fg: '#fff',
      border: STEP_GREEN,
      title,
      titleColor: '#6b7280',
      titleWeight: 500,
    };
  }
  if (stg === pos) {
    // 現在地
    let color = STAGE_COLOR[current];
    let mark = String(pos);
    if (pos === 3) {
      color = current === 'approved' ? STEP_GREEN : STEP_RED;
      mark = current === 'approved' ? '✓' : '✕';
    }
    return { mark, bg: color, fg: '#fff', border: color, title, titleColor: color, titleWeight: 700 };
  }
  // 未到達
  return {
    mark: String(pos),
    bg: '#fff',
    fg: '#9ca3af',
    border: '#d1d5db',
    title,
    titleColor: '#9ca3af',
    titleWeight: 500,
  };
};

const StepperRow = styled.div`
  display: flex;
  align-items: center;
  flex: 0 1 420px;
`;

const StepCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 88px;
`;

const StepCircle = styled.div<{ $bg: string; $fg: string; $border: string }>`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font: 700 14px system-ui;
  background: ${({ $bg }) => $bg};
  color: ${({ $fg }) => $fg};
  border: 2px solid ${({ $border }) => $border};
`;

const StepTitle = styled.div<{ $color: string; $weight: number }>`
  font-size: 13px;
  color: ${({ $color }) => $color};
  font-weight: ${({ $weight }) => $weight};
`;

const StepConnector = styled.div<{ $active: boolean; $color: string }>`
  flex: 1;
  height: 2px;
  margin: 0 -6px 26px;
  background: ${({ $active, $color }) => ($active ? $color : '#e5e7eb')};
`;

const HoldBadge = styled.div`
  font: 600 10.5px system-ui;
  color: #b7791f;
  background: #fef6e7;
  border: 1px solid #f6d98a;
  padding: 2px 8px;
  border-radius: 20px;
`;

// 審査ステータスのステッパー（受付 → 審査 → 完了 の3段）。保留は審査段のサブ状態として扱う。
export const ReviewStepper = ({ current }: { current: ReviewStatus }) => {
  const a = makeStep(1, current);
  const b = makeStep(2, current);
  const c = makeStep(3, current);
  const stg = statusStage(current);
  return (
    <StepperRow aria-label="審査ステータス">
      <StepCol>
        <StepCircle $bg={a.bg} $fg={a.fg} $border={a.border}>
          {a.mark}
        </StepCircle>
        <StepTitle $color={a.titleColor} $weight={a.titleWeight}>
          {a.title}
        </StepTitle>
      </StepCol>
      <StepConnector $active={stg >= 2} $color={STEP_GREEN} />
      <StepCol>
        <StepCircle $bg={b.bg} $fg={b.fg} $border={b.border}>
          {b.mark}
        </StepCircle>
        <StepTitle $color={b.titleColor} $weight={b.titleWeight}>
          {b.title}
        </StepTitle>
        {current === 'pending' && <HoldBadge>保留中</HoldBadge>}
      </StepCol>
      <StepConnector $active={stg >= 3} $color={current === 'rejected' ? STEP_RED : STEP_GREEN} />
      <StepCol>
        <StepCircle $bg={c.bg} $fg={c.fg} $border={c.border}>
          {c.mark}
        </StepCircle>
        <StepTitle $color={c.titleColor} $weight={c.titleWeight}>
          {c.title}
        </StepTitle>
      </StepCol>
    </StepperRow>
  );
};

// ステッパーと文脈アクション（審査を開始 / 審査を再開）を横並びにするヘッダ。
export const StepperHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
  margin: 16px 0 24px;
`;

// 審査中の下部固定バー（すべて読んだうえで保存・保留・判定する）。
// フォーム外に置くことで、長いフォームをスクロール中も下端に固定される。
export const DecisionBar = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  padding: 16px 4px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.05);
`;

export const DecisionGuide = styled.span`
  ${({ theme }) => css`
    ${theme.typography.body3.regular}
    color: ${theme.semanticColors.text.secondary};
    margin-right: auto;
  `}
`;

// 審査フローの決定ボタン。無効時の表現を全 variant 共通のグレーに統一し、
// 「今は押せない」を一目で分かるようにする（共有 Buttons.tsx は変更せず審査画面だけに適用）。
// 既定の disabled 表現は variant ごとにまちまち（danger/warning は色が濃く残る）で分かりにくいため。
export const DecisionButton = styled(Button)`
  &:disabled {
    background: #f0f0f0 !important;
    border: 1px solid #e0e0e0 !important;
    color: #b0b0b0 !important;
    box-shadow: none !important;
    opacity: 1 !important;
    cursor: not-allowed !important;
  }
`;

const DiscardModalBody = styled.div`
  margin-top: 16px;
  text-align: center;
`;

const DiscardModalText = styled.p`
  ${({ theme }) => css`
    ${theme.typography.body3.regular}
    color: ${theme.semanticColors.text.secondary};
  `}
`;

const DiscardModalActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
`;

// 未保存の編集を破棄する前の確認ダイアログ。加盟店/店舗の審査画面で共有する。
export const DiscardChangesModal = ({
  isOpen,
  onCancel,
  onConfirm,
}: {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => (
  <ConfirmModal
    isModalOpen={isOpen}
    closeModal={onCancel}
    title="変更を破棄しますか？"
    form={
      <DiscardModalBody>
        <DiscardModalText>編集内容は失われ、最後に保存した状態に戻ります。</DiscardModalText>
        <DiscardModalActions>
          <Button type="button" variant="neutral" onClick={onCancel}>
            キャンセル
          </Button>
          <Button type="button" variant="danger" onClick={onConfirm}>
            破棄
          </Button>
        </DiscardModalActions>
      </DiscardModalBody>
    }
  />
);

const TerminalNoteBox = styled.div<{ $color: string; $bg: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ $color }) => $color}33;
`;

const TerminalDot = styled.span<{ $color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const TerminalLabel = styled.span<{ $color: string }>`
  font: 700 13.5px system-ui;
  color: ${({ $color }) => $color};
`;

const TerminalDesc = styled.span`
  ${({ theme }) => css`
    ${theme.typography.body3.regular}
    color: ${theme.semanticColors.text.secondary};
  `}
`;

// 確定済み(承認/却下)の結果表示。entityLabel は「加盟店」「店舗」など。
export const TerminalNote = ({
  status,
  entityLabel,
}: {
  status: ReviewStatus;
  entityLabel: string;
}) => {
  if (status !== 'approved' && status !== 'rejected') return null;
  const approved = status === 'approved';
  const color = approved ? STEP_GREEN : STEP_RED;
  return (
    <TerminalNoteBox $color={color} $bg={approved ? '#eaf6eb' : '#fdecea'}>
      <TerminalDot $color={color} />
      <TerminalLabel $color={color}>{approved ? '承認済み' : '却下'}</TerminalLabel>
      <TerminalDesc>
        {approved
          ? `この申請は承認され、${entityLabel}が有効になっています。`
          : 'この申請は却下されました。'}
      </TerminalDesc>
    </TerminalNoteBox>
  );
};
